import { Chat as ChatIcon } from "@mui/icons-material";
import type { ColumnDef } from "@tanstack/react-table";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AIChat, { type AIChatHandle } from "~/components/aiChat";
import ChatHistoryPanel from "~/components/aiChat/components/ChatHistoryPanel";
import CommonDocumentHeader from "~/components/Document/commonDocumentHeader";
import ExportModal, { type TableExportData } from "~/components/Document/exportModal";
import Button from "~/components/ui/button";
import { TOAST_MESSAGES } from "~/constants/messages";
import PluginProposalTable from "~/features/plugin/pluginProposalTable";
import { AIChatApi } from "~/functions/api/aiChat";
import { DocumentApi } from "~/functions/api/document";
import { useNavigationPrompt } from "~/functions/navigationHooks";
import { useProjectMembers } from "~/hooks/useProjectMembers";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import DeleteIcon from "~/images/delete_row.svg?react";
import type {
  PluginProposalAttibutes,
  PluginProposalData,
  PluginProposalTableHearder,
} from "~/interface/plugin";
import {
  acquireLock,
  addRow,
  clearError,
  deleteRow,
  fetchPluginProposal,
  releaseLock,
  restoreFromCopyData,
  updatePluginProposal,
  updateRow,
} from "~/slices/pluginProposalSlice";
import { initializeSessions } from "~/slices/projectSlice";
import type { AppDispatch, RootState } from "~/store";
import chatStyles from "../systemProposal/styles.module.scss";
import styles from "./index.module.scss";

export const PluginProposalHeaders = Object.keys({
  fid: "",
  必要機能: "",
  プラグイン名: "",
  機能一覧: "",
  価格情報: "",
  会社名: "",
  提案理由: "",
  マッチ度: "",
  URL: "",
}) as PluginProposalTableHearder;

export const DisplayPluginProposalHeaders = Object.keys({
  必要機能: "",
  プラグイン名: "",
  機能一覧: "",
  価格情報: "",
  会社名: "",
  提案理由: "",
}) as PluginProposalTableHearder;

interface DocumentInfo {
  createdAt: string;
  modifiedAt: string;
  modifiedBy?: {
    display_name: string;
  };
}

const Plugin = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedId } = useParams<{ selectedId: string }>();

  const { error: projectError } = useProjectMembers();

  const { data, isEditable, isLoading, error } = useSelector(
    (state: RootState) => state.pluginProposal,
  );
  const { currentUser, currentProject, organization } = useSelector(
    (state: RootState) => state.auth,
  );
  const navigate = useNavigate();
  const { canAccessChat } = useProjectPermission();
  const [showExportModal, setShowExportModal] = useState(false);

  // チャットアイコンの表示可否を判定
  const shouldShowChatIcon = canAccessChat();
  const [documentInfo, setDocumentInfo] = useState<DocumentInfo | null>(null);

  // AIChat関連の状態
  const { currentSession } = useSelector((state: RootState) => state.project);
  const [showAIChat, setShowAIChat] = React.useState(true);
  const [showChatHistory, setShowChatHistory] = React.useState(false);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [threads, setThreads] = React.useState<any[]>([]);
  const aiChatRef = React.useRef<AIChatHandle>(null);
  const [currentThreadId, setCurrentThreadId] = React.useState<string>("");

  useEffect(() => {
    const fetchDocumentInfo = async () => {
      if (!currentProject || !organization) return;

      try {
        const documentApi = new DocumentApi();
        const response = await documentApi.get(
          organization.id,
          currentProject.id,
          currentProject.outputs?.["functional-requirements"]?.id!,
        );

        if (response) {
          if (response.modified_by) {
            setDocumentInfo({
              createdAt: response.created_at || "",
              modifiedAt: response.modified_at || "",
              modifiedBy: response.modified_by,
            });
          } else {
            setDocumentInfo({
              createdAt: response.created_at || "",
              modifiedAt: response.modified_at || "",
            });
          }
        }
      } catch (error) {
        console.error("Failed to fetch document info:", error);
      }
    };

    fetchDocumentInfo();
  }, [currentProject, organization]);

  const toggleEditMode = () => {
    if (isEditable) {
      handleUpdate(); // 保存処理を実行
    } else {
      handleLock();
    }
  };

  const handleLock = async () => {
    await dispatch(acquireLock());
  };

  const handleCancel = async () => {
    await dispatch(releaseLock());
    dispatch(restoreFromCopyData());
  };

  const handleUpdate = async () => {
    // Immediately update the modifiedAt time
    if (documentInfo) {
      setDocumentInfo({
        ...documentInfo,
        modifiedAt: new Date().toISOString(),
      });
    }
    await dispatch(updatePluginProposal());
  };

  // 編集中の場合、ナビゲーションをブロック
  useNavigationPrompt(
    isEditable,
    "編集内容が保存されていません。ページを離れてもよろしいですか？",
    handleUpdate,
    handleCancel,
  );

  const updateData = (rowIndex: number, columnId: PluginProposalAttibutes, value: string) => {
    const updatedRow = { ...data[rowIndex]!, [columnId]: value };
    dispatch(updateRow({ index: rowIndex, data: updatedRow }));
  };

  const addNewRow = () => {
    const newRow: PluginProposalData = PluginProposalHeaders.reduce((acc, key) => {
      acc[key as keyof PluginProposalData] = "";
      return acc;
    }, {} as PluginProposalData);

    dispatch(addRow(newRow));
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isEditable) {
        if (!currentProject?.outputs?.["plugin-proposal"]?.id) {
          toast.error(TOAST_MESSAGES.DOCUMENT.PLUGIN_NOT_FOUND);
          return;
        }

        // カスタムメッセージ（古いブラウザでは表示される）
        event.preventDefault();
        event.returnValue = ""; // Chrome用: 空文字を指定
      }
    };

    // beforeunload イベントの登録
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // クリーンアップ
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isEditable, currentProject]);

  const handleExportClick = () => {
    setShowExportModal(true);
  };

  const getExportData = (): TableExportData => {
    const name = "プラグイン提案書";
    const rows = data.map((row) =>
      PluginProposalHeaders.map((header) => row[header as keyof PluginProposalData] || ""),
    );
    const headers = PluginProposalHeaders;
    return { headers, rows, name };
  };

  const columns: ColumnDef<PluginProposalData>[] = [
    ...(isEditable
      ? PluginProposalHeaders.filter((header) => {
          return header !== "URL" && header !== "マッチ度" && header !== "fid";
        }).map((header) => ({
          accessorKey: header as keyof PluginProposalData,
          header: header,
        }))
      : DisplayPluginProposalHeaders.map((header) => ({
          accessorKey: header as keyof PluginProposalData,
          header: header,
        }))),
    {
      accessorKey: "delete",
      header: "",
      cell: ({ row }) =>
        isEditable ? (
          <button
            onClick={() => dispatch(deleteRow(row.index))}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
            }}
          >
            <DeleteIcon width={16} height={16} />
          </button>
        ) : null,
    },
  ];

  useEffect(() => {
    if (!currentProject) {
      navigate(`/${currentUser.organization_id}/home`);
      return;
    }

    // URLパラメータから取得、なければReduxストアから取得
    const documentId = selectedId || currentProject?.outputs?.["plugin-proposal"]?.id;

    if (!documentId) {
      toast.error(TOAST_MESSAGES.DOCUMENT.PLUGIN_NOT_FOUND);
      navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      return;
    }

    dispatch(fetchPluginProposal(documentId)).then((result) => {
      if (result.type === "pluginProposal/fetch/rejected") {
        navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      }
    });
  }, [dispatch, currentProject, currentUser, navigate, selectedId]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (projectError) {
      console.error("Project data loading error:", projectError);
    }
  }, [projectError]);

  // Initialize session IDs (reinit on document change)
  const pluginProposalDocId = currentProject?.outputs?.["plugin-proposal"]?.id;
  useEffect(() => {
    if (currentProject?.id && currentUser?.id) {
      dispatch(initializeSessions(pluginProposalDocId || ""));
    }
  }, [currentProject?.id, currentUser?.id, pluginProposalDocId, dispatch]);

  // currentSession.threadId が変更されたら currentThreadId を更新
  useEffect(() => {
    if (currentSession?.threadId) {
      setCurrentThreadId(currentSession.threadId);
    }
  }, [currentSession?.threadId]);

  // 履歴を更新する共通関数
  const refreshThreadHistory = async () => {
    // スレッド作成時や手動リロード時に履歴を更新
    setHistoryLoading(true);
    try {
      const api = new AIChatApi();
      const agentId = "commonAgent";
      const rid = currentSession?.resourceId;
      if (!rid) return;
      const list = await api.getThreads(agentId, rid, 10);
      setThreads(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("[Plugin] Failed to refresh thread history:", e);
    } finally {
      setHistoryLoading(false);
    }
  };

  if (isLoading) {
    return <div>ロード中...</div>;
  }
  if (error) return <div>{error}</div>;

  const handleHistoryClick = () => {
    const pathSegments = window.location.pathname.split("/");
    pathSegments.pop();
    navigate(`${pathSegments.join("/")}/history`);
  };

  return (
    <div className={styles["plugin"]}>
      {documentInfo && (
        <CommonDocumentHeader
          title="プラグイン提案書"
          documentType="plugin-proposal"
          createdAt={documentInfo.createdAt}
          modifiedAt={documentInfo.modifiedAt}
          {...(documentInfo.modifiedBy && { modifiedBy: documentInfo.modifiedBy })}
          isEditable={isEditable}
          isLoading={isLoading}
          handleCancel={handleCancel}
          toggleEditMode={toggleEditMode}
          handleExportClick={handleExportClick}
          handleHistoryClick={handleHistoryClick}
          showEditButton={true}
          showExportButton={true}
        />
      )}
      {showExportModal && (
        <ExportModal
          documentType="plugin-proposal"
          onClose={() => setShowExportModal(false)}
          exportData={{
            type: "table",
            exportData: getExportData(),
            name: "plugin-proposal",
          }}
        />
      )}

      <div
        style={{
          display: "flex",
          gap: "24px",
          alignItems: "stretch",
          position: "relative",
          minHeight: "calc(100vh - 196px)",
        }}
      >
        <div style={{ flex: "1 1 auto" }}>
          <div style={{ overflowX: "auto" }}>
            <PluginProposalTable
              data={data}
              columns={columns}
              updateData={updateData}
              isEditable={isEditable}
            />
          </div>
          {isEditable && (
            <Button label="追加" onClick={addNewRow} styleType="add-row" icon="addRowIcon" />
          )}
        </div>
        {showAIChat && (
          <div style={{ display: "flex", alignItems: "stretch", flex: "0 0 auto" }}>
            <ChatHistoryPanel
              open={showChatHistory}
              placement="sidebar"
              title="履歴（最新10件）"
              loading={historyLoading}
              threads={threads}
              activeThreadId={currentThreadId}
              onClose={() => setShowChatHistory(false)}
              onRefresh={refreshThreadHistory}
              onLoadThread={async (t) => {
                const tid = t.id || t.threadId;
                if (tid) {
                  setCurrentThreadId(tid);
                }
                await aiChatRef.current?.loadThread(t, "agent");
              }}
            />
            <AIChat
              ref={aiChatRef}
              isOpen={showAIChat}
              onClose={() => setShowAIChat(false)}
              onPreviewDiff={() => {}}
              height="calc(100vh - 196px)"
              maxHeight="none"
              currentDocumentId={currentProject?.outputs?.["plugin-proposal"]?.id || ""}
              currentProjectId={currentProject?.id || ""}
              currentDocumentType="plugin-proposal"
              originalDocument=""
              threadId={currentSession?.threadId}
              resourceId={currentSession?.resourceId}
              onOpenHistory={async () => {
                setShowChatHistory(true);
                await refreshThreadHistory();
              }}
              onThreadUpdate={refreshThreadHistory}
              onThreadChange={(tid) => setCurrentThreadId(tid)}
            />
          </div>
        )}
      </div>

      {!showAIChat && !isEditable && shouldShowChatIcon && (
        <div className={chatStyles["floatingButtons"]}>
          <button
            className={chatStyles["chatButton"]}
            onClick={() => setShowAIChat(true)}
            aria-label="AIチャットを開く"
          >
            <ChatIcon />
          </button>
        </div>
      )}
    </div>
  );
};

export default Plugin;
